import React from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import RichText from '../RichText'
import {
  HeroTextTwoColumnSectionProps,
  HTSVariants,
  AlignmentVariants,
} from '../../types/heroTextSection'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import useBrand from '../../hooks/useBrand'
import { insertHandlebarsTemplate } from '../../utils/insertHandlebarsTemplate'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'
import {
  BindToFields,
  CommonClasses,
  SuccessStoryClasses,
} from '../../types/contentfulAttributes'
import { createContentfulClassGetter } from '../../utils/contentful'

const HeroTextSectionColumn: React.FC<HeroTextTwoColumnSectionProps> = ({
  displayClass = '',
  titleClass,
  row,
  sectionClass,
}) => {
  const { classes, cx } = useStyles()
  const brand = useBrand()
  const handlebarsContext = useHandlebarsContext()
  const applyContentfulStyles = createContentfulClassGetter(row.css)

  return (
    <Box
      id="heroTextSectionColumn"
      className={cx(
        displayClass,
        classes.columnsContainer,
        {
          [classes.unPadded]: displayClass.includes('ss-3-packages'),
          // @ts-ignore
          [sectionClass]:
            !!sectionClass &&
            row.variant !== HTSVariants.ColumnHero &&
            row.variant !== HTSVariants.ReverseColumnHero,
          [classes.mainPadding]:
            row.variant !== HTSVariants.ColumnHero &&
            row.variant !== HTSVariants.ReverseColumnHero,
          [classes.heroPadding]:
            row.variant === HTSVariants.ColumnHero ||
            row.variant === HTSVariants.ReverseColumnHero,
          [classes.paddingTop]: row.paddingUpdate === 'ZeroTopPadding',
          [classes.paddingBottomZero]:
            row.paddingUpdate === 'ZeroBottomPadding',
          [classes.paddingZero]: row.paddingUpdate === 'ZeroTopBottomPadding',
          [classes.everythingYouNeedToFind]: row.codeClasses?.includes(
            'everything-you-need-to-find'
          ),
        },
        applyContentfulStyles(BindToFields.Main)
      )}
    >
      <Box
        className={cx(
          {
            [classes.column]: true,
            [classes.columnLeft]: true,
            [classes.heroLeft]: row.variant === HTSVariants.ColumnHero,
            [classes.heroLeftZJ]:
              row.variant === HTSVariants.ColumnHero && brand.name === 'ZipJob',
            [classes.colPadded]: displayClass.includes('ss-3-packages'),
            [classes.columnWide]:
              row.column1Alignment === AlignmentVariants.Wide,
          },
          applyContentfulStyles(BindToFields.WrapperTwoColumnOne)
        )}
      >
        {row.subtitle && (
          <Typography component="h2" className={titleClass}>
            {insertHandlebarsTemplate(row.subtitle, handlebarsContext)}
          </Typography>
        )}
        <Box
          className={cx(
            {
              [classes.alignmentLeft]:
                row.column1Alignment === AlignmentVariants.Left,
              [classes.alignmentCenter]:
                row.column1Alignment === AlignmentVariants.Center,
              [classes.alignmentRight]:
                row.column1Alignment === AlignmentVariants.Right,
              [classes.alignmentWide]:
                row.column1Alignment === AlignmentVariants.Wide,
              [classes.removeCollapsing]: true,
              [classes.ss3Cta]: displayClass.includes('ss-3-packages'),
            },
            applyContentfulStyles(BindToFields.WrapperColumnOne)
          )}
        >
          <RichText
            cssEntries={row.css}
            rawBody={row.column1.raw}
            references={row.column1.references}
            options={{
              props: {
                [BLOCKS.HEADING_1]: {
                  className: cx({
                    [classes.contentPadding]: brand.name === 'ZipJob',
                    [classes.heroH1]:
                      row.variant === HTSVariants.ColumnHero &&
                      brand.name === 'ZipJob',
                  }),
                },
                [BLOCKS.HEADING_2]: {
                  className: cx({
                    [classes.contentPadding]: brand.name === 'ZipJob',
                    [classes.paddingBottomZero]: brand.name !== 'ZipJob',
                  }),
                },
                [BLOCKS.PARAGRAPH]: {
                  className: cx({
                    [classes.contentPadding]: brand.name === 'ZipJob',
                    [classes.marginLeft]: true,
                    [classes.successStoryParas]: displayClass.includes(
                      SuccessStoryClasses.LandingPageColumn1
                    ),
                  }),
                },
                [BLOCKS.UL_LIST]: {
                  className: cx({
                    [classes.ul]: brand.name === 'ZipJob',
                    [classes.ulCloser]: brand.name !== 'ZipJob',
                  }),
                },
                [INLINES.ASSET_HYPERLINK]: {
                  className: cx({
                    [classes.contentPadding]: true,
                  }),
                },
                [BLOCKS.QUOTE]: {
                  className: cx({
                    [classes.quote]: displayClass.includes('ss-3-packages'),
                  }),
                },
              },
            }}
          />
        </Box>
      </Box>
      <Box
        className={cx(
          {
            [classes.column]: true,
            [classes.imageContentNarrow]:
              row.variant === HTSVariants.Column ||
              row.variant === HTSVariants.ReverseColumn,
            [classes.imageContentWide]:
              row.variant === HTSVariants.ColumnWideImage ||
              row.variant === HTSVariants.ReverseColumnWideImage,
            [classes.heroImage]:
              row.variant === HTSVariants.ColumnHero ||
              row.variant === HTSVariants.ReverseColumnHero,
            [classes.heroImageTR]:
              (row.variant === HTSVariants.ColumnHero ||
                row.variant === HTSVariants.ReverseColumnHero) &&
              brand.name !== 'ZipJob',
            [classes.alignmentLeft]:
              row.column2Alignment === AlignmentVariants.Left,
            [classes.alignmentCenter]:
              row.column2Alignment === AlignmentVariants.Center,
            [classes.alignmentRight]:
              row.column2Alignment === AlignmentVariants.Right,
            [classes.aboutImage]: row.codeClasses?.includes(
              CommonClasses.AboutImage
            ),
          },
          applyContentfulStyles(BindToFields.WrapperColumnTwo)
        )}
      >
        <RichText
          cssEntries={row.css}
          rawBody={row.column2.raw}
          references={row.column2.references}
        />
      </Box>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  alignmentCenter: {
    textAlign: 'center',
  },
  alignmentLeft: {
    textAlign: 'left',
  },
  alignmentRight: {
    textAlign: 'right',
  },
  alignmentWide: {
    textAlign: 'left',
  },
  column: {
    [theme.breakpoints.down('md')]: {
      margin: '.75rem 0',
    },
  },
  columnsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: 'auto',
  },
  unPadded: {
    '&&': {
      paddingBlock: 0,
    },
  },
  ss3Cta: {
    '& > p:last-of-type': {
      display: 'grid',
      gridTemplateColumns: '0.55fr 0.45fr',
      alignItems: 'center',
      gap: '1rem',

      '& > a': {
        marginLeft: 0,
        fontWeight: 'bold',
      },
    },
  },
  quote: {
    '& > p': {
      fontSize: '1.06325rem',

      '&:before': {
        content: 'open-quote',
        position: 'absolute',
        transform: 'translate(-110%, -20%)',
        fontSize: '2.5em',
        color: theme.colors.blue[400],
      },

      '&:after': {
        content: 'close-quote',
        position: 'absolute',
        transform: 'translate(5%, -20%)',
        fontSize: '2.5em',
        color: theme.colors.blue[400],
      },

      [theme.breakpoints.up('lg')]: {
        fontSize: '1.5625rem',
      },
    },
  },
  marginLeft: {
    '& > a:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('sm')]: {
        margin: '1rem 0',
      },
    },
    '& > button:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('sm')]: {
        margin: '1rem 0',
      },
    },
    '& > a': {
      [theme.breakpoints.down('sm')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
    '& > button': {
      [theme.breakpoints.down('sm')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
  },
  aboutImage: {
    '& > img ': {
      [theme.breakpoints.down('lg')]: {
        maxWidth: '100%',
        height: 'auto',
      },
    },
  },
  contentPadding: {
    padding: '0.5625rem 0',
  },
  columnLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '43%',
    paddingRight: '3rem',
    [theme.breakpoints.down('lg')]: {
      width: '45%',
      paddingRight: '0rem',
    },
    [theme.breakpoints.down('md')]: {
      paddingRight: '0rem',
      width: '100%',
    },
  },
  everythingYouNeedToFind: {
    width: '90%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    '& .gatsby-image-wrapper': {
      // Use a class selector with dot notation
      marginLeft: '10%',
    },
  },

  columnWide: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '80%',
    [theme.breakpoints.down('lg')]: {
      width: '70%',
      paddingRight: '4rem',
    },
    [theme.breakpoints.down('md')]: {
      paddingRight: '0rem!important',
      width: '100%',
      paddingLeft: '0rem!important',
    },
  },
  colPadded: {
    padding: '4% 2.5%',
  },
  heroH1: {
    [theme.breakpoints.down('md')]: {
      fontSize: '2.13em',
    },
  },

  heroImage: {
    height: '100%',
    width: '50%',
    [theme.breakpoints.down('lg')]: {
      '& > div': {
        height: '100%',
        maxHeight: 'unset',
      },
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  heroImageTR: {
    [theme.breakpoints.down('md')]: {
      display: 'initial',
      order: -1,
      width: '100%',
    },
  },
  heroLeft: {
    padding: '2em 6%',
    width: '50%',
    [theme.breakpoints.down('lg')]: {
      paddingRight: '1em',
    },
    [theme.breakpoints.down('md')]: {
      padding: '2.2em 6% 1.96em 6%',
      width: '100%',
    },
    '@media (min-width: 1456px)': {
      padding: '2em 6% 2em calc((100% - 91rem)/2 + 5.5rem)',
    },
  },
  heroLeftZJ: {
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
    },
  },
  heroPadding: {
    padding: '0',
  },
  imageContentNarrow: {
    alignSelf: 'center',
    '&&': {
      width: '43%',
      maxWidth: '31.68rem !important',
      [theme.breakpoints.down('md')]: {
        maxHeight: 'unset !important',
        maxWidth: 'unset !important',
        width: '100%',
      },
    },
  },
  imageContentWide: {
    '&&': {
      width: '50%',
      maxWidth: '38.56rem !important',
      maxHeight: '38.56rem !important',
      [theme.breakpoints.down('md')]: {
        maxHeight: 'initial !important',
        maxWidth: 'initial !important',
        width: '100%',
      },
    },
  },
  mainPadding: {
    paddingBottom: '4rem',
    paddingTop: '4rem',
  },
  ul: {
    paddingInlineStart: '1.2em',
  },
  ulCloser: {
    marginTop: '0',

    '& p': {
      padding: '.35em 0',
    },
  },
  paddingTop: {
    paddingTop: '0',
  },
  paddingBottomZero: {
    paddingBottom: '0',
  },
  paddingZero: {
    paddingTop: '0',
    paddingBottom: '0',
  },
  successStoryParas: {
    '&:last-child': {
      marginTop: '1.75rem',
      display: 'grid',
      gap: '0.5rem',

      '& .contentful-class-page-link-button': {
        paddingLeft: '0.65rem',
        paddingRight: '0.65rem',
      },

      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: '0.6fr 0.4fr',
        gap: '0.75rem',
      },

      [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: '0.47fr 0.53fr',
      },
    },
  },
  removeCollapsing: {
    display: 'grid', // To remmove margin collapsing

    '& > a[role="button"]': {
      justifySelf: 'baseline',
    },
  },
}))

export default HeroTextSectionColumn
